/**
 * RefundDialog Component
 * 
 * Modal dialog for processing refunds with amount and reason selection
 */
import React, { useState } from 'react';
import { 
  X,
  DollarSign,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../common';
import { H3, Text } from '../../../common/Typography';
import { Button } from '../../../common';
import { paymentService } from '../../../services/api/paymentService';

const RefundDialog = ({ payment, maxRefundAmount, onClose, onSuccess }) => {
  const [refundAmount, setRefundAmount] = useState(maxRefundAmount || 0);
  const [refundReason, setRefundReason] = useState('requested_by_customer');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  if (!payment || !payment.id) {
    return (
      <div className="text-center py-12 text-gray-500">
        <RefreshCw className="h-8 w-8 mx-auto mb-3 text-gray-300" />
        <p className="font-medium">No payment selected</p>
        <p className="text-sm mt-1">Select a payment from the list to process a refund.</p>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount / 100);
  };

  const refundReasons = [
    { value: 'requested_by_customer', label: 'Requested by customer' },
    { value: 'duplicate', label: 'Duplicate payment' },
    { value: 'fraudulent', label: 'Fraudulent transaction' },
    { value: 'subscription_cancelled', label: 'Subscription cancelled' },
    { value: 'product_unacceptable', label: 'Product unacceptable' },
    { value: 'product_not_received', label: 'Product not received' },
    { value: 'unrecognised', label: 'Unrecognised transaction' },
    { value: 'credit_not_processed', label: 'Credit not processed' },
    { value: 'general', label: 'General' },
    { value: 'processing_error', label: 'Processing error' }
  ];

  const handleRefund = async () => {
    if (refundAmount <= 0 || refundAmount > maxRefundAmount) {
      setError('Invalid refund amount');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const result = await paymentService.processRefund(payment.id, {
        amount: refundAmount,
        reason: refundReason
      });

      if (result.refund || result.payment) {
        onSuccess(result.payment || { ...payment, status: 'refunded' });
      } else if (result.success === false) {
        setError(result.error || 'Failed to process refund');
      } else {
        onSuccess(result);
      }
    } catch (err) {
      console.error('Refund error:', err);
      setError('Failed to process refund. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const isFullRefund = refundAmount === maxRefundAmount;
  const isPartialRefund = refundAmount > 0 && refundAmount < maxRefundAmount;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Process Refund
          </CardTitle>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Payment Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <Text className="text-sm text-gray-600">Payment ID:</Text>
              <Text className="text-sm font-mono">#{payment.id.substring(0, 8)}</Text>
            </div>
            <div className="flex items-center justify-between mb-2">
              <Text className="text-sm text-gray-600">Customer:</Text>
              <Text className="text-sm">{payment.customerEmail}</Text>
            </div>
            <div className="flex items-center justify-between mb-2">
              <Text className="text-sm text-gray-600">Original Amount:</Text>
              <Text className="text-sm font-medium">{formatCurrency(payment.amount)}</Text>
            </div>
            <div className="flex items-center justify-between">
              <Text className="text-sm text-gray-600">Available to Refund:</Text>
              <Text className="text-sm font-medium text-green-600">
                {formatCurrency(maxRefundAmount)}
              </Text>
            </div>
          </div>

          {/* Refund Amount */}
          <div>
            <label htmlFor="refundAmount" className="block text-sm font-medium text-gray-700 mb-2">
              Refund Amount
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                id="refundAmount"
                type="number"
                min="1"
                max={maxRefundAmount}
                step="1"
                value={refundAmount}
                onChange={(e) => setRefundAmount(parseInt(e.target.value) || 0)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter amount in cents"
              />
            </div>
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => setRefundAmount(maxRefundAmount)}
                className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
              >
                Full Refund ({formatCurrency(maxRefundAmount)})
              </button>
              <button
                onClick={() => setRefundAmount(Math.floor(maxRefundAmount / 2))}
                className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
              >
                Half ({formatCurrency(Math.floor(maxRefundAmount / 2))})
              </button>
            </div>
            <Text className="text-xs text-gray-500 mt-1">
              Amount in cents. Preview: {formatCurrency(refundAmount)}
            </Text>
          </div>

          {/* Refund Reason */}
          <div>
            <label htmlFor="refundReason" className="block text-sm font-medium text-gray-700 mb-2">
              Refund Reason
            </label>
            <select
              id="refundReason"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {refundReasons.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>

          {/* Warning for partial refunds */}
          {isPartialRefund && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-yellow-800">
                <AlertTriangle className="h-4 w-4" />
                <Text className="text-sm font-medium">Partial Refund</Text>
              </div>
              <Text className="text-xs text-yellow-700 mt-1">
                This will refund {formatCurrency(refundAmount)} of the original {formatCurrency(payment.amount)} payment.
                The remaining {formatCurrency(maxRefundAmount - refundAmount)} will still be available for future refunds.
              </Text>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="h-4 w-4" />
                <Text className="text-sm font-medium">Error</Text>
              </div>
              <Text className="text-xs text-red-700 mt-1">{error}</Text>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRefund}
              className="flex-1 flex items-center justify-center gap-2"
              disabled={isProcessing || refundAmount <= 0 || refundAmount > maxRefundAmount}
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  {isFullRefund ? 'Full Refund' : 'Partial Refund'}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RefundDialog;
